import { Box, Text } from '@fullstory/components';
import { Option } from '@fullstory/components/components/RadioOption';
import { ActivityTab, PAGES_TALLY_COLLECTION_TS, ReferencesTab } from '@fullstory/dqm-ui';
import { DetailModalPropertiesTable } from '@fullstory/events-ui';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { DependentEventFilter, Event, EventFilter } from '@fullstory/proto/fs/api/segment';
import { DQMRow, VolumeTimeRange } from '@fullstory/proto/fs/services/dx-objects';
import { ValidationError } from '@fullstory/proto/fs/services/paige';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import {
  ActiveIndexContext,
  AppearancesContextWithOnPreview,
  OnPreviewFnWithIndex,
  SignalStudioComponentProps,
  SignalStudioContext,
} from '@fullstory/signal-studio-context';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageDefForm } from '../components/PageDefForm';
import { usePageDefForm } from '../components/PageDefForm/usePageDefForm';
import { Mode, RejectionSuggestionOptions } from '../constants';
import { PageDefinitionForm } from '../types';
import { formPattern } from '../utils/form';
import { makePageLabelDeps, makePagePatternDeps } from '../utils/signal-studio';
import { usePageDefDependencies } from './usePageDefDependencies';
import { usePageDetailView } from './usePageDetailView';
import EventSupport = DQMRow.EventSupport;
import { assertExhaustive } from '@fullstory/typeutils';

export type PageDefSignalStudioProps = {
  page: PageDefinition;
  setPage: (page: PageDefinition) => void;
  initialErrors: ValidationError[] | undefined;
  onClose: (saved?: boolean) => void;
  initialSpecSlugs?: string[];
  disableEdit?: boolean;
  disableArchive?: boolean;
  isSuggestion?: boolean;
} & ReturnType<typeof usePageDefDependencies>;

const primary: EventFilter = { click: { any: {} } };

export const usePageDefSignalStudio = (
  props: PageDefSignalStudioProps,
): {
  SignalStudio: React.ComponentType<SignalStudioComponentProps> | null;
  signalStudioProps: SignalStudioComponentProps;
} => {
  const intl = useIntl();
  const SignalStudio = useContext(SignalStudioContext);
  const pageDefinition = props.page;
  const [pageId, setPageId] = useState(pageDefinition.id);
  const flags = useFlags();
  const canModifyPages = flags.hasBit(Flag.PermModifyPageDefinitions);
  const usePagesReferences = flags.featureFlags().AppObjectReferencesPages;
  const usesFeedback = flags.featureFlags().AppAiPageSuggestionsFeedback;
  const [activeIndex, setActiveIndex] = useState(0);
  const [timeRange, setTimeRange] = useState(VolumeTimeRange.DAY_7);

  const rejectSuggestionOptions: Option<string>[] = [
    {
      id: RejectionSuggestionOptions.TOO_BROAD,
      label: intl.formatMessage({ defaultMessage: 'Page definition too broad', id: 'lg9Uxj' }),
    },
    {
      id: RejectionSuggestionOptions.TOO_GRANULAR,
      label: intl.formatMessage({ defaultMessage: 'Page definition too granular', id: 'Wbt7yG' }),
    },
    {
      id: RejectionSuggestionOptions.MISLEADING,
      label: intl.formatMessage({
        defaultMessage: 'Misleading or incorrect page suggestion',
        id: 'z4iBDv',
      }),
    },
    {
      id: RejectionSuggestionOptions.DUPLICATIVE,
      label: intl.formatMessage({
        defaultMessage: 'Page definition is duplicative',
        id: '0ID8mY',
      }),
    },
    {
      id: RejectionSuggestionOptions.OTHER,
      label: intl.formatMessage({ defaultMessage: 'Other', id: '/VnDMl' }),
    },
  ];

  const [selectedFeedbackOption, setSelectedFeedbackOption] = useState<Option<string>>(
    rejectSuggestionOptions[0] ?? {
      id: RejectionSuggestionOptions.TOO_BROAD,
      label: intl.formatMessage({ defaultMessage: 'Page definition too broad', id: 'lg9Uxj' }),
    },
  );

  useEffect(() => {
    setPageId(pageDefinition.id);
  }, [pageDefinition.id]);

  const {
    hostnames,
    getSuggestions,
    getPageById,
    onSave,
    onArchive,
    onRestore,
    canArchiveProperties,
    propertiesLoading,
    propertiesFailed,
    properties,
    getConnections,
    onRejectSuggestion,
  } = usePageDefDependencies(pageDefinition.id);

  const { data } = usePageDetailView({ pageId: `${pageId}`, timeRange });

  const onRestoreWithConflict = (id: number) => {
    // we're going to set errors downstream
    const onConflict = () => null;
    return onRestore(id, onConflict);
  };

  const {
    mode,
    form,
    setForm,
    page,
    errors,
    handleChange,
    save,
    restore,
    archive,
    onConflictingRulesEdit,
    onConflictingRulesArchive,
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    existingSpecObjects,
    snoozed,
    onSnoozeChange,
    rejectSuggestion,
  } = usePageDefForm(
    pageDefinition,
    props.setPage,
    props.initialErrors || [],
    props.initialSpecSlugs || null,
    (pageDef: PageDefinition, fsSpecMappingSlugs: string[] | null) =>
      onSave(pageDef, () => props.onClose(true), fsSpecMappingSlugs, {
        prev: data?.details?.silence,
        next: snoozed,
      }),
    () => props.onClose(false),
    onArchive,
    onRestoreWithConflict,
    getPageById,
    data?.details?.silence || null,
    (id: string) =>
      onRejectSuggestion?.(id, () => {
        props.onClose(false);
      }),
  );

  const tabs = useMemo(() => {
    if (!pageId) {
      return undefined;
    }

    const titles = [
      intl.formatMessage({ defaultMessage: 'Properties', id: 'aI80kg' }),
      intl.formatMessage({ defaultMessage: 'Activity', id: 'ZmlNQ3' }),
    ];
    const content = [
      {
        key: 'properties',
        node: (
          <DetailModalPropertiesTable
            connectionParent={page.id}
            properties={properties}
            canArchive={canArchiveProperties}
            loading={propertiesLoading}
            failed={propertiesFailed}
            retryFailed={getConnections}
            existingSpecObjects={existingSpecObjects}
          />
        ),
      },
      {
        key: 'activity',
        node: (
          <ActivityTab
            activityMonitoring={data?.activityMonitoring || null}
            activityMonitoringError={data?.activityMonitoringError || null}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            objectType={intl.formatMessage({ defaultMessage: 'Page', id: 'wFmAFC' })}
            monitoringStart={PAGES_TALLY_COLLECTION_TS}
            auditHistory={null}
            eventSupport={EventSupport.EVENT_SUPPORT_SUPPORTED}
          />
        ),
      },
    ];
    if (usePagesReferences) {
      titles.push(intl.formatMessage({ defaultMessage: 'References', id: 'nCQY4D' }));
      content.push({
        key: 'references',
        node: <ReferencesTab objectReferences={data?.objectReferences || null} objectType="Page" />,
      });
    }
    return { titles, content };
  }, [
    pageId,
    intl,
    page.id,
    properties,
    canArchiveProperties,
    propertiesLoading,
    propertiesFailed,
    getConnections,
    data?.activityMonitoring,
    data?.activityMonitoringError,
    timeRange,
    data?.objectReferences,
    usePagesReferences,
    existingSpecObjects,
  ]);

  const [events, setEvents] = useState<Array<{ event: Event | null }>>([]);

  const header = useMemo(() => {
    if (pageId) {
      return <FormattedMessage defaultMessage="Edit Page" id="6JL8M8" />;
    }

    return <FormattedMessage defaultMessage="Create Page" id="XGRBwY" />;
  }, [pageId]);

  const makeDeps = useCallback(
    (form: PageDefinitionForm): DependentEventFilter[][] =>
      // A page could contain both patterns and API page definitions.
      // The form preserves ordering and should determine the deps.
      form.definition.value.length
        ? form.definition.value.map(v => {
            switch (v.type) {
              case 'pattern':
                return makePagePatternDeps(formPattern(v), flags);
              case 'label':
                return [makePageLabelDeps(v.pageName)];
              default:
                assertExhaustive(v);
            }
          })
        : [[]],
    [flags],
  );

  useEffect(() => {
    const deps = makeDeps(form);
    // deps will be an empty array if there's not a domain AND path - don't show a preview for those!
    const newEvents = deps.map(deps => ({ event: deps.length > 0 ? { deps, primary } : null }));

    // manual debounce that is always applied as some changes we want to set immediately
    const handler = setTimeout(() => {
      setEvents(newEvents);
    }, 800);

    // but if length changed, let's set that immediately for other UX affordances
    setEvents(currentEvents => {
      if (currentEvents.length !== newEvents.length) {
        return newEvents;
      }
      return currentEvents;
    });

    return () => {
      clearTimeout(handler);
    };
  }, [form, flags]);

  const onDeleteIndex = useCallback(
    (index: number) => {
      if ((index === activeIndex && activeIndex > 0) || index < activeIndex) {
        setActiveIndex(activeIndex - 1);
      }
    },
    [activeIndex, setActiveIndex],
  );

  const onPreview: OnPreviewFnWithIndex = useCallback(
    (_, __, deps, index) => {
      setEvents(events => {
        const newEvents = [...events];
        const existingDeps = makeDeps(form)[index] ?? [];
        const newDeps = [...existingDeps, ...deps]; // we want to indersect the url rule deps and the preview deps
        const newEvent = { primary, deps: newDeps };
        newEvents[activeIndex] = { event: newEvent };
        return newEvents;
      });
    },
    [setEvents, activeIndex, form],
  );

  const ConfigSidebar = (
    <AppearancesContextWithOnPreview onPreview={onPreview}>
      <ActiveIndexContext.Provider
        value={{ index: activeIndex, setIndex: setActiveIndex, deleteIndex: onDeleteIndex }}
      >
        <Box marginBottom={20}>
          <Text appearance="heading1">{header}</Text>
        </Box>
        <PageDefForm
          mode={mode}
          form={form}
          setForm={setForm}
          errors={errors}
          handleChange={handleChange}
          onConflictingRulesEdit={onConflictingRulesEdit}
          onConflictingRulesArchive={onConflictingRulesArchive}
          hostnameOptions={hostnames}
          getSuggestions={getSuggestions}
          getPageById={getPageById}
          selectedFsSpecObjectSlugs={selectedFsSpecObjectSlugs}
          setSelectedFsSpecObjectSlugs={setSelectedFsSpecObjectSlugs}
          fsSpecEnabled={fsSpecEnabled}
          fsSpecRoleOptions={fsSpecRoleOptions}
          existingSpecObjects={existingSpecObjects}
          pageDefOnTop
          issues={data?.details?.issuesData?.issues || []}
          snoozed={data?.details?.silence || null}
          setSnooze={onSnoozeChange}
          preventOverflow
        />
      </ActiveIndexContext.Provider>
    </AppearancesContextWithOnPreview>
  );

  const metadata = useMemo(() => {
    if (!pageDefinition.metadata || !page.id) {
      return undefined;
    }

    return {
      createdBy: pageDefinition.metadata.createdBy,
      updatedBy: pageDefinition.metadata.lastUpdatedBy,
      updatedAt: pageDefinition.metadata.lastUpdated,
      createdAt: pageDefinition.metadata.created,
    };
  }, [page.id, pageDefinition.metadata]);

  return {
    SignalStudio,
    signalStudioProps: {
      header: form.name.value ?? '',
      lowerConfigPartName: intl.formatMessage({ defaultMessage: 'page rule', id: 'B9Tvt4' }),
      configSidebar: ConfigSidebar,
      tabs,
      preview: {
        index: activeIndex,
        onIndexChange: setActiveIndex,
        mode: 'page-preview',
        previews: events,
      },
      onConfirm: save,
      onCloseSignal: () => {
        props.onClose(false);
      },
      onArchive: page.id && !props.isSuggestion ? archive : undefined,
      onUnarchive: page.id ? restore : undefined,
      onRejectSuggestion: () => {
        if (props.isSuggestion && usesFeedback && page.suggestion) {
          rejectSuggestion(page.suggestion.id, selectedFeedbackOption.label);
        }
      },
      rejectionOptions: usesFeedback
        ? {
            feedbackOptions: rejectSuggestionOptions,
            onSelectFeedbackOption: setSelectedFeedbackOption,
            selectedFeedbackOption,
            shouldShowFeedbackFreeText:
              selectedFeedbackOption.id === RejectionSuggestionOptions.OTHER,
          }
        : undefined,
      isSuggestion: props.isSuggestion,
      archived: pageDefinition.metadata?.archived || false,
      disableEdit: props.disableEdit || pageDefinition.metadata?.archived || !canModifyPages,
      disableArchive:
        (mode === Mode.create || props.disableArchive || props.disableEdit || !canModifyPages) &&
        !props.isSuggestion,
      emptyMessage: intl.formatMessage(
        {
          defaultMessage: 'Page Preview<b></b>Start defining your page to see a preview',
          id: 'Q/0jka',
        },
        {
          b: () => <br />,
        },
      ),
      metadata,
    },
  };
};
